import { Button } from "@common/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@common/components/ui/dropdown-menu"
import { Label } from "@common/components/ui/label"
import { Switch } from "@common/components/ui/switch"
import { PLANNING } from "@common/flags"
import { cn } from "@common/lib/utils"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useRef } from "react"
import { FilterBar } from "@/components/layout/filter-bar"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import useCustomToast from "@/hooks/use-custom-toast"
import { requireFeatureFlag } from "@/lib/require-feature-flag"
import { requirePermission } from "@/lib/require-permission"
import BarChart from "~icons/material-symbols/bar-chart"
import ChevronLeft from "~icons/material-symbols/chevron-left"
import ChevronRight from "~icons/material-symbols/chevron-right"
import ExpandMore from "~icons/material-symbols/expand-more"
import { CapacityPanel } from "./-components/capacity-panel"
import { CrewView } from "./-components/crew-view"
import { DayColumns } from "./-components/grid"
import { TimelineHeader } from "./-components/timeline-header"
import { WorkOrderView } from "./-components/work-order-view"
import { scheduleFor } from "./-lib/schedule"
import {
  manpowerSearchSchema,
  useManpowerSearch,
  useUpdateManpowerSearch,
  VIEWS,
  viewLabel,
} from "./-lib/search"
import {
  buildTimeline,
  defaultStart,
  RANGES,
  type Range,
  rangeLabel,
  rangeStep,
} from "./-lib/timeline"

export const Route = createFileRoute("/_layout/manpower/")({
  staticData: { breadcrumb: "Manpower" },
  validateSearch: (search) => manpowerSearchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "work_package:read", "/")
    await requireFeatureFlag(queryClient, PLANNING)
  },
  component: ManpowerPage,
})

function ManpowerPage(): React.ReactElement {
  const search = Route.useSearch()
  const range = search.range ?? "quarter"
  const start = search.start ?? defaultStart(range)
  const timeline = useMemo(() => buildTimeline(range, start), [range, start])
  const schedule = scheduleFor(search.quoted ?? true)
  const chartRef = useRef<HTMLDivElement>(null)

  const syncChart = (event: React.UIEvent<HTMLDivElement>) => {
    if (chartRef.current) {
      chartRef.current.scrollLeft = event.currentTarget.scrollLeft
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <FilterBar>
        <Toolbar />
      </FilterBar>
      <div className="min-h-0 flex-1 overflow-auto" onScroll={syncChart}>
        <div className="min-w-max pb-6">
          <TimelineHeader timeline={timeline} />
          <div className="relative">
            <DayColumns timeline={timeline} />
            {search.view === "work-orders" ? (
              <WorkOrderView timeline={timeline} schedule={schedule} />
            ) : (
              <CrewView timeline={timeline} schedule={schedule} />
            )}
          </div>
        </div>
      </div>
      {(search.capacity ?? true) && (
        <CapacityPanel
          timeline={timeline}
          schedule={schedule}
          scrollRef={chartRef}
        />
      )}
    </div>
  )
}

function Toolbar(): React.ReactElement {
  const search = useManpowerSearch()
  const update = useUpdateManpowerSearch()

  return (
    <ListToolbar
      tabs={
        <div className="flex items-center gap-3">
          <ListTabs
            tabs={VIEWS}
            tab={search.view ?? "crew"}
            label={viewLabel}
            onSelect={(view) =>
              update({ view: view === "crew" ? undefined : view })
            }
          />
          <QuotedSwitch />
        </div>
      }
      actions={
        <>
          <DateStepper />
          <RangeMenu />
          <NameFilter
            search={search.search ?? ""}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search people, tails…"
          />
          <CapacityToggle />
          <PublishButton />
        </>
      }
    />
  )
}

function QuotedSwitch(): React.ReactElement {
  const { quoted = true } = useManpowerSearch()
  const update = useUpdateManpowerSearch()
  return (
    <div
      className="flex items-center gap-2"
      title="Include work that is quoted but not yet won"
    >
      <Switch
        id="include-quoted"
        checked={quoted}
        onCheckedChange={(next) => update({ quoted: next ? undefined : false })}
      />
      <Label htmlFor="include-quoted" className="whitespace-nowrap">
        Include quoted work
      </Label>
    </div>
  )
}

function DateStepper(): React.ReactElement {
  const search = useManpowerSearch()
  const update = useUpdateManpowerSearch()
  const range = search.range ?? "quarter"
  const start = search.start ?? defaultStart(range)
  const step = (direction: number) =>
    update({ start: start + direction * rangeStep(range) })

  return (
    <div className="flex items-center">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Previous"
        onClick={() => step(-1)}
      >
        <ChevronLeft />
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => update({ start: undefined })}
      >
        Today
      </Button>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Next"
        onClick={() => step(1)}
      >
        <ChevronRight />
      </Button>
    </div>
  )
}

function RangeMenu(): React.ReactElement {
  const { range = "quarter" } = useManpowerSearch()
  const update = useUpdateManpowerSearch()

  const pick = (value: string) => {
    const next = RANGES.find((option) => option === value)
    if (next)
      update({ range: next === "quarter" ? undefined : next, start: undefined })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm">
          {rangeLabel(range)}
          <ExpandMore className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={range} onValueChange={pick}>
          {RANGES.map((option: Range) => (
            <DropdownMenuRadioItem key={option} value={option}>
              {rangeLabel(option)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function CapacityToggle(): React.ReactElement {
  const { capacity = true } = useManpowerSearch()
  const update = useUpdateManpowerSearch()
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      aria-label="Capacity panel"
      aria-pressed={capacity}
      className={cn(capacity && "bg-muted")}
      onClick={() => update({ capacity: capacity ? false : undefined })}
    >
      <BarChart />
    </Button>
  )
}

function PublishButton(): React.ReactElement {
  const showToast = useCustomToast()
  return (
    <Button
      size="sm"
      onClick={() =>
        showToast(
          "Schedule published",
          "The crew can see the new schedule.",
          "success",
        )
      }
    >
      Publish schedule
    </Button>
  )
}
